/**
 * Custom hook for Roles Management
 * Handles state and API calls for role CRUD operations
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import { rbacService, type RolesScope } from '@/services/rbac/rbacService';
import type { Role, Feature, Action, CreateRoleRequest, UpdateRoleRequest } from '@/types/rbac';
import { useAuth } from '@/store/authStore';
import { useTranslation } from '@/store/languageStore';
import toast from 'react-hot-toast';
import { categorizeFeatures, MenuCategory } from '@/types/roles';

const LEGACY_ACTION_CODES = new Set<string>([
  'ACCESS', 'ACTIVATE_BATCH', 'CANCEL_SUBSCRIPTION', 'CONNECT', 'FETCH_CARINFO',
  'FORCE_DEACTIVATE', 'PID_SETTINGS', 'RELEASE', 'SUBSCRIBE', 'VIEW_CONGESTION_TAX',
  'VIEW_PROPERTIES', 'EXPORT_PDF',
]);

const SYSTEM_ACTION_CODES = new Set<string>([
  'VIEW', 'ADD', 'CREATE', 'EDIT', 'UPDATE', 'DELETE', 'DEACTIVATE', 'ACTIVATE',
  'IMPORT', 'EXPORT', 'ASSIGN', 'UNASSIGN', 'START', 'COMPLETE', 'CANCEL', 'APPROVE',
  'REJECT', 'SEND', 'READ', 'WRITE', 'MANAGE', 'SYNC', 'RUN', 'TOGGLE',
  'BOOK_FOR_OTHERS', 'CREATE_VEHICLE', 'UPDATE_VEHICLE', 'DELETE_VEHICLE', 'MANAGE_VEHICLE_STATUS',
  'APPLY_CONFIG', 'CONFIGURE_DEVICE', 'EXPORT_REPORT',
  'RETURN', 'RETURN_OVERRIDE', 'RETURN_OFFSITE', 'EXTEND_EXPIRED', 'POST_RETURN_ACCESS', 'CHECKOUT', 'UNLOCK', 'LOCK',
  'CHANGE_RESOURCE', 'CREATE_BLOCK', 'EDIT_BLOCK', 'RELEASE_BLOCK',
  'OBD_AUTO', 'OBD_ELD', 'OBD_NON_OBD', 'OBD_ANTI_ALARM', 'OBD_DISABLE',
]);

const GENERIC_ACTION_NAMES = new Set(['Skapa', 'Redigera', 'Radera', 'Exportera']);

/** Plocka ut backendens felmeddelande (response.data.error) före axios generiska text. */
const apiErrorMessage = (error: unknown): string => {
  const err = error as { response?: { data?: { error?: string } }; message?: string };
  return err?.response?.data?.error || err?.message || '';
};

function dedupeActionsByCode(actions: Action[]): Action[] {
  const byCode = new Map<string, Action>();
  for (const action of actions) {
    const code = String(action.code || '').trim().toUpperCase();
    if (!code) continue;
    const existing = byCode.get(code);
    if (!existing) {
      byCode.set(code, action);
      continue;
    }
    const score = (a: Action) => {
      const generic = GENERIC_ACTION_NAMES.has(String(a.name || '').trim());
      const idNum = Number(a.id);
      return (generic ? 0 : 100) + (Number.isFinite(idNum) ? -idNum : 0);
    };
    if (score(action) > score(existing)) {
      byCode.set(code, action);
    }
  }
  return Array.from(byCode.values()).sort((a, b) =>
    String(a.code).localeCompare(String(b.code))
  );
}

const isSystemActionCode = (code?: string | null): boolean => {
  const normalized = String(code || '').trim().toUpperCase();
  if (!normalized) return false;
  if (LEGACY_ACTION_CODES.has(normalized)) return false;
  if (SYSTEM_ACTION_CODES.has(normalized)) return true;
  if (
    normalized.startsWith('CREATE_') ||
    normalized.startsWith('UPDATE_') ||
    normalized.startsWith('DELETE_') ||
    normalized.startsWith('VIEW_') ||
    normalized.startsWith('MANAGE_') ||
    normalized.startsWith('APPLY_') ||
    normalized.startsWith('CONFIGURE_') ||
    normalized.startsWith('EXPORT_')
  ) {
    return true;
  }
  return false;
};

export type RolesManagementMode = RolesScope;

export const useRolesManagement = (mode: RolesManagementMode = 'organization') => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const isGlobalAdmin = user?.is_global_admin;
  const isSystemMode = mode === 'system';
  const orgScoped = !isSystemMode;

  const [roles, setRoles] = useState<Role[]>([]);
  const [roleTemplates, setRoleTemplates] = useState<Role[]>([]);
  const [features, setFeatures] = useState<Feature[]>([]);
  const [actions, setActions] = useState<Map<string, Action[]>>(new Map());
  const [loading, setLoading] = useState(true);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);

  const [formData, setFormData] = useState<CreateRoleRequest>({
    code: '',
    name: '',
    description: '',
    is_role_administrator: false,
    permissions: [],
  });
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');

  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const [selectedFeatureId, setSelectedFeatureId] = useState<string | null>(null);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);
  const [showInactive, setShowInactive] = useState(true);

  const normalizeId = (value: string | number | null | undefined): string =>
    value === null || value === undefined ? '' : String(value);

  const buildActionsMap = useCallback((allActionsData: Record<string, Action[]>) => {
    const actionsMap = new Map<string, Action[]>();
    for (const [featureId, featureActions] of Object.entries(allActionsData)) {
      const normalizedActions = (featureActions || [])
        .map((action) => ({
          ...action,
          id: normalizeId(action.id),
          feature_id: normalizeId(action.feature_id),
        }))
        .filter((action) => isSystemActionCode(action.code));
      actionsMap.set(normalizeId(featureId), dedupeActionsByCode(normalizedActions));
    }
    return actionsMap;
  }, []);

  const categorizedFeatures: MenuCategory[] = useMemo(
    () => categorizeFeatures(features),
    [features]
  );

  const toggleCategory = useCallback((categoryId: string) => {
    setExpandedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(categoryId)) {
        newSet.delete(categoryId);
      } else {
        newSet.add(categoryId);
      }
      return newSet;
    });
  }, []);

  const loadFeaturesAndActions = useCallback(async () => {
    if (isSystemMode && isGlobalAdmin) {
      const [featuresData, allActionsData] = await Promise.all([
        rbacService.getFeatures(),
        rbacService.getAllActions(),
      ]);
      const normalizedFeatures = featuresData.map((f) => ({ ...f, id: normalizeId(f.id) }));
      setFeatures(normalizedFeatures);
      setActions(buildActionsMap(allActionsData));
    } else {
      const [featuresData, allActionsData] = await Promise.all([
        rbacService.getAvailableFeatures(orgScoped),
        rbacService.getAvailableActions(orgScoped),
      ]);
      const normalizedFeatures = featuresData.map((f) => ({ ...f, id: normalizeId(f.id) }));
      setFeatures(normalizedFeatures);
      setActions(buildActionsMap(allActionsData));
    }
  }, [isSystemMode, isGlobalAdmin, orgScoped, buildActionsMap]);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const rolesData = await rbacService.getRoles(showInactive, mode);
      setRoles(rolesData);
      await loadFeaturesAndActions();
      if (!isSystemMode) {
        const templates = await rbacService.getRoleTemplates();
        setRoleTemplates(templates);
      }
    } catch (error: any) {
      toast.error(t('roles.loadDataError') + ': ' + apiErrorMessage(error));
    } finally {
      setLoading(false);
    }
  }, [showInactive, mode, isSystemMode, t, loadFeaturesAndActions]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const applyTemplatePermissions = useCallback(async (templateId: string) => {
    if (!templateId) return;
    try {
      const permissions = await rbacService.getRoleTemplatePermissions(templateId);
      setFormData(prev => ({
        ...prev,
        template_role_id: templateId,
        permissions: permissions.map(p => ({
          feature_id: normalizeId(p.feature_id),
          action_id: p.action_id ? normalizeId(p.action_id) : undefined,
        })),
      }));
    } catch (error: any) {
      toast.error(t('roles.loadTemplateError') + ': ' + apiErrorMessage(error));
    }
  }, [t]);

  const handleTemplateChange = useCallback(async (templateId: string) => {
    setSelectedTemplateId(templateId);
    await applyTemplatePermissions(templateId);
  }, [applyTemplatePermissions]);

  const handleCreateRole = useCallback(async () => {
    try {
      if (!formData.code || !formData.name) {
        toast.error(t('roles.codeAndNameRequired'));
        return;
      }
      if (!isSystemMode && !formData.template_role_id) {
        toast.error(t('roles.templateRequired'));
        return;
      }

      await rbacService.createRole(
        isSystemMode
          ? { ...formData, organization_id: undefined }
          : { ...formData },
        mode
      );

      toast.success(t('roles.roleCreated'));
      setShowCreateModal(false);
      setFormData({ code: '', name: '', description: '', is_role_administrator: false, permissions: [] });
      setSelectedTemplateId('');
      await loadData();
    } catch (error: any) {
      toast.error(t('roles.createRoleError') + ': ' + apiErrorMessage(error));
    }
  }, [formData, isSystemMode, mode, t, loadData]);

  const handleToggleRoleActive = useCallback(async (role: Role) => {
    try {
      await rbacService.toggleRoleActive(role.id, !role.is_active);
      toast.success(role.is_active ? t('roles.roleDeactivated') : t('roles.roleActivated'));
      await loadData();
    } catch (error: any) {
      toast.error(t('roles.toggleError') + ': ' + apiErrorMessage(error));
    }
  }, [t, loadData]);

  const handleTogglePermission = useCallback((featureId: string, actionId?: string) => {
    const normalizedFeatureId = normalizeId(featureId);
    const normalizedActionId = actionId ? normalizeId(actionId) : undefined;
    setFormData(prev => {
      const existing = prev.permissions.find(
        p => normalizeId(p.feature_id) === normalizedFeatureId && normalizeId(p.action_id) === normalizeId(normalizedActionId)
      );
      if (existing) {
        return {
          ...prev,
          permissions: prev.permissions.filter(
            p => !(normalizeId(p.feature_id) === normalizedFeatureId && normalizeId(p.action_id) === normalizeId(normalizedActionId))
          ),
        };
      }
      return {
        ...prev,
        permissions: [...prev.permissions, { feature_id: normalizedFeatureId, action_id: normalizedActionId }],
      };
    });
  }, []);

  const handleToggleAllActionsForFeature = useCallback((featureId: string) => {
    const normalizedFeatureId = normalizeId(featureId);
    const hasAllActions = formData.permissions.some(
      p => normalizeId(p.feature_id) === normalizedFeatureId && !p.action_id
    );
    if (hasAllActions) {
      setFormData(prev => ({
        ...prev,
        permissions: prev.permissions.filter(p => normalizeId(p.feature_id) !== normalizedFeatureId),
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        permissions: [
          ...prev.permissions.filter(p => normalizeId(p.feature_id) !== normalizedFeatureId),
          { feature_id: normalizedFeatureId },
        ],
      }));
    }
  }, [formData.permissions]);

  const openEditModal = useCallback(async (role: Role) => {
    if (!role?.id) {
      toast.error(t('roles.loadRoleError') || 'Ogiltig roll');
      return;
    }
    try {
      const fullRole = await rbacService.getRoleById(role.id);
      setSelectedRole(fullRole);
      const permissions = fullRole.permissions?.map(p => ({
        feature_id: normalizeId(p.feature_id),
        action_id: p.action_id ? normalizeId(p.action_id) : undefined,
      })) || [];
      setFormData({
        code: fullRole.code,
        name: fullRole.name,
        description: fullRole.description || '',
        is_role_administrator:
          fullRole.is_role_administrator === true ||
          fullRole.is_role_administrator === 1 ||
          String(fullRole.is_role_administrator) === '1',
        permissions,
        template_role_id: fullRole.template_role_id ? normalizeId(fullRole.template_role_id) : undefined,
      });
      if (actions.size === 0 && features.length > 0) {
        await loadFeaturesAndActions();
      }
      const permFeatureIds = new Set(permissions.map(p => normalizeId(p.feature_id)));
      const autoExpand = new Set<string>();
      for (const category of categorizedFeatures) {
        if (category.features.some(f => permFeatureIds.has(normalizeId(f.id)))) {
          autoExpand.add(category.id);
        }
      }
      setExpandedCategories(autoExpand);
      setShowEditModal(true);
    } catch (error: any) {
      toast.error(t('roles.loadRoleError') + ': ' + apiErrorMessage(error));
    }
  }, [t, actions.size, features.length, categorizedFeatures, loadFeaturesAndActions]);

  const handleUpdateRole = useCallback(async () => {
    if (!selectedRole) return;
    try {
      const updateData: UpdateRoleRequest = {
        name: formData.name,
        description: formData.description,
        is_role_administrator: formData.is_role_administrator === true,
        permissions: formData.permissions,
      };
      await rbacService.updateRole(selectedRole.id, updateData);
      toast.success(t('roles.roleUpdated'));
      setShowEditModal(false);
      setSelectedRole(null);
      await loadData();
    } catch (error: any) {
      toast.error(t('roles.updateRoleError') + ': ' + apiErrorMessage(error));
    }
  }, [selectedRole, formData, t, loadData]);

  const hasPermission = useCallback((featureId: string, actionId?: string): boolean => {
    const normalizedFeatureId = normalizeId(featureId);
    const normalizedActionId = actionId ? normalizeId(actionId) : undefined;
    return formData.permissions.some(
      p => normalizeId(p.feature_id) === normalizedFeatureId && normalizeId(p.action_id) === normalizeId(normalizedActionId)
    );
  }, [formData.permissions]);

  const resetForm = useCallback(() => {
    setFormData({ code: '', name: '', description: '', is_role_administrator: false, permissions: [] });
    setSelectedTemplateId('');
    setExpandedCategories(new Set());
    setSelectedFeatureId(null);
    setSelectedCategoryId(null);
    setSelectedRole(null);
  }, []);

  return {
    mode,
    isSystemMode,
    roles,
    roleTemplates,
    features,
    actions,
    loading,
    isGlobalAdmin,
    categorizedFeatures,
    showCreateModal,
    setShowCreateModal,
    showEditModal,
    setShowEditModal,
    selectedRole,
    formData,
    setFormData,
    selectedTemplateId,
    handleTemplateChange,
    expandedCategories,
    selectedFeatureId,
    setSelectedFeatureId,
    selectedCategoryId,
    setSelectedCategoryId,
    showInactive,
    setShowInactive,
    toggleCategory,
    handleCreateRole,
    handleToggleRoleActive,
    handleTogglePermission,
    handleToggleAllActionsForFeature,
    openEditModal,
    handleUpdateRole,
    hasPermission,
    resetForm,
  };
};
